import { computeAnalysis, type AnalysisComponent, type AnalysisKind, type MeasurementRow } from '../estimate/analysis.js';
import { D, roundTo } from '../decimal.js';

/**
 * Devis exemple : immeuble R+4 de 10 logements (5 F4, 5 F3), maçonnerie en parpaings, couverture en
 * tuile métallique. Géométrie, pré-dimensionnement et prix sont des hypothèses de démonstration, chacune
 * avec sa source : ce modèle sert à découvrir l'application, jamais à construire ni à remettre une offre.
 */
export const R4_SOURCES = {
  PRESSE: "Prix indicatif · Tchadinfos, quincailleries de N'Djamena, 13/07/2026",
  SABLE: 'Prix indicatif · Tchadinfos, Ordre des architectes, 06/08/2026 (6 m³ pour 110 000 à 120 000)',
  ABD: "Prix indicatif · devis réel à N'Djamena, 04/11/2024",
  HYPOTHESE: 'Prix indicatif · estimation, à ajuster selon vos prix réels',
} as const;
const { PRESSE, SABLE, ABD, HYPOTHESE } = R4_SOURCES;

export const R4_REFERENCE = 'DEMO-R4';

export const R4_NOTICE =
  "EXEMPLE DE DÉMONSTRATION, NON CONTRACTUEL. PRIX À TITRE INDICATIF : ajustez-les selon vos prix réels avant toute offre. Quantités et dimensions sont aussi des hypothèses. " +
  'Fondations pré-dimensionnées pour un sol supposé de 1,5 bar : elles ne valent ni étude de sol ni note de calcul, et doivent être validées par un ingénieur avant toute construction. ' +
  'Prix : ciment, bois et sable relevés dans la presse tchadienne en 2026, granulats, parpaings, aciers et main-d\'œuvre issus d\'un projet de 2024, tous les autres estimés. La source de chaque prix figure dans le sous-détail de la ligne.';

export const R4_PROJECT = {
  name: 'DÉMO — Immeuble R+4, 10 logements F3 et F4',
  reference: R4_REFERENCE,
  ownerName: "Maître d'ouvrage de démonstration",
  location: "N'Djamena, Tchad (exemple)",
  buildingType: 'Immeuble de logements',
  grossArea: '1200',
  description: "Emprise 20 × 12 m, 5 niveaux de 240 m², hauteur d'étage 3,20 m. Par niveau : un F4 et un F3 autour d'une cage d'escalier.",
  observations: R4_NOTICE,
  currency: 'XAF',
} as const;

export const R4_SETTINGS = { vatEnabled: true, vatRate: '0.18', contingencyRate: '0.05', siteOverheadRate: '0.05', generalOverheadRate: '0.05', marginRate: '0.08' } as const;

/** Descente de charges retenue pour le pré-dimensionnement : voir `R4_FOUNDATIONS`. */
export const R4_SOIL_BAR = '1.5';

/** Semelles sous 24 poteaux, trame 4 × 4 m. Surface = charge de service ÷ 150 kPa ; hauteur ≈ (B − b) / 4 + 5 cm, poteau b = 35 cm. */
export const R4_FOUNDATIONS = [
  { code: 'S1', position: 'centrale', count: 8, surfaceTributaire: '16', chargeKn: '900', side: '2.50', height: '0.60' },
  { code: 'S2', position: 'de rive', count: 12, surfaceTributaire: '8', chargeKn: '480', side: '1.80', height: '0.45' },
  { code: 'S3', position: "d'angle", count: 4, surfaceTributaire: '4', chargeKn: '240', side: '1.30', height: '0.35' },
] as const;

export interface R4Line {
  lot: string;
  designation: string;
  unit: string;
  justification?: string;
  rows: MeasurementRow[];
  components: AnalysisComponent[];
}

const n = (v: number) => String(v);
const row = (label: string, count: number, l: number | string, w?: number | string, h?: number | string): MeasurementRow => ({
  label,
  count: n(count),
  length: String(l),
  ...(w !== undefined ? { width: String(w) } : {}),
  ...(h !== undefined ? { height: String(h) } : {}),
});
const minus = (r: MeasurementRow): MeasurementRow => ({ ...r, deduct: true });
const val = (label: string, value: number, count = 1): MeasurementRow => ({ label, count: n(count), value: n(value) });

const c = (kind: AnalysisKind, designation: string, unit: string, quantity: number, unitPrice: number, source: string, lossRate?: number): AnalysisComponent => ({
  kind,
  designation,
  unit,
  quantity: String(quantity),
  unitPrice: String(unitPrice),
  source,
  ...(lossRate ? { lossRate: String(lossRate) } : {}),
});
const mat = (designation: string, unit: string, quantity: number, unitPrice: number, source: string, lossRate?: number) => c('MATERIAL', designation, unit, quantity, unitPrice, source, lossRate);
const mo = (designation: string, unit: string, unitPrice: number, source: string = ABD, quantity = 1) => c('LABOR', `Main-d'œuvre : ${designation}`, unit, quantity, unitPrice, source);

/** Fourniture et pose d'un ouvrage sans détail : une seule hypothèse, assumée comme telle. */
const fp = (fourniture: number, pose: number, unit: string, nom = 'Fourniture') => [mat(nom, unit, 1, fourniture, HYPOTHESE), mo('pose', unit, pose, HYPOTHESE)];

const CIMENT = 11000;
const SABLE_M3 = 19000;
const GRAVIER = 40000;
const ACIER_KG = 720;
const ACIER_SRC = `${ABD}, barres HA ramenées au kg`;

/** Béton par m³ : 400 L de sable et 800 L de gravier environ, ciment selon le dosage. */
const beton = (sacs: number) => [
  mat('Ciment CEM (sac de 50 kg)', 'sac', sacs, CIMENT, PRESSE),
  mat('Sable', 'm³', 0.45, SABLE_M3, SABLE),
  mat('Gravier', 'm³', 0.85, GRAVIER, ABD),
];
const aciers = (kg: number) => [mat('Aciers HA façonnés', 'kg', kg, ACIER_KG, ACIER_SRC, 0.05), mat("Fil d'attache", 'kg', kg * 0.015, 1500, ABD)];
/** Coffrage bois avec réemplois, par m² de coffrage rapporté au m³ de béton. */
const coffrage = (m2: number) => [
  mat('Planches de coffrage (réemplois compris)', 'U', m2 * 0.25, 14000, PRESSE),
  mat('Chevrons et étais', 'U', m2 * 0.15, 9000, PRESSE),
  mat('Pointes', 'kg', m2 * 0.2, 2000, ABD),
];
const ba = (kgAcier: number, m2Coffrage: number, sacs = 7) => [...beton(sacs), ...aciers(kgAcier), ...coffrage(m2Coffrage), mo('béton armé', 'm³', 25000)];

const mortierM2 = (sacs: number, sable: number) => [mat('Ciment CEM (sac de 50 kg)', 'sac', sacs, CIMENT, PRESSE), mat('Sable', 'm³', sable, SABLE_M3, SABLE)];

const fouilles = [
  row('Semelles S1, surlargeur de 30 cm', 8, '3.10', '3.10', '1.60'),
  row('Semelles S2', 12, '2.40', '2.40', '1.60'),
  row('Semelles S3', 4, '1.90', '1.90', '1.60'),
  row('Tranchées de longrines', 1, 152, '0.50', '0.60'),
];
const semelles = R4_FOUNDATIONS.map((s) => row(`${s.code}, semelle ${s.position}`, s.count, s.side, s.side, s.height));
const proprete = [row('Sous S1', 8, '2.70', '2.70', '0.10'), row('Sous S2', 12, '2.00', '2.00', '0.10'), row('Sous S3', 4, '1.50', '1.50', '0.10')];
const amorces = [row('Sur S1', 8, '0.35', '0.35', '0.90'), row('Sur S2', 12, '0.35', '0.35', '1.05'), row('Sur S3', 4, '0.35', '0.35', '1.15')];
const longrines = [row('Axes : 4 files de 20 m et 6 files de 12 m', 1, 152, '0.25', '0.40')];

const OUVERTURES_EXT = [minus(row('Fenêtres 1,20 × 1,20 m', 45, '1.20', '1.20')), minus(row('Fenêtres 0,80 × 0,80 m', 30, '0.80', '0.80')), minus(row("Porte d'immeuble", 1, '1.80', '2.40'))];
const PORTES_INT = [minus(row('Portes intérieures', 55, '0.80', '2.10')), minus(row('Portes palières', 10, '1.00', '2.10'))];
const PORTES_INT_2FACES = [minus(row('Portes intérieures, 2 faces', 110, '0.80', '2.10')), minus(row('Portes palières, 2 faces', 20, '1.00', '2.10'))];
const FACADE = [row('Façades, soubassement compris', 1, 64, '16.60')];
const MURS_INT = [
  row('Face intérieure des murs de façade', 5, '59.20', '2.75'),
  row('Refends, 2 faces', 10, 20, '2.75'),
  row('Cloisons, 2 faces', 10, 58, '2.75'),
];

export const R4_LINES: R4Line[] = [
  // ── Installation et études ──
  { lot: 'PREP', designation: 'Installation et repli de chantier', unit: 'FF', rows: [val('Forfait', 1)], components: [mat('Baraques, clôture provisoire, eau et électricité de chantier', 'FF', 1, 3500000, HYPOTHESE)] },
  { lot: 'PREP', designation: 'Étude de sol et note de calcul béton armé', unit: 'FF', justification: 'À réaliser avant toute construction : elle remplace le pré-dimensionnement de cet exemple.', rows: [val('Forfait', 1)], components: [mat('Honoraires', 'FF', 1, 3000000, HYPOTHESE)] },
  { lot: 'PREP', designation: 'Implantation du bâtiment', unit: 'FF', rows: [val('Forfait', 1)], components: [mo('implantation', 'FF', 50000)] },
  { lot: 'PREP', designation: 'Nettoyage et repli en fin de chantier', unit: 'FF', rows: [val('Forfait', 1)], components: [mo('nettoyage', 'FF', 500000, HYPOTHESE)] },

  // ── Terrassement ──
  { lot: 'TERR', designation: 'Fouilles en puits et en tranchées', unit: 'm³', justification: 'Profondeur de 1,50 m plus 10 cm de béton de propreté, à confirmer par l\'étude de sol.', rows: fouilles, components: [mo('fouille', 'm³', 3000)] },
  {
    lot: 'TERR',
    designation: 'Remblai des fouilles en terre du site, compacté',
    unit: 'm³',
    rows: [...fouilles, ...semelles.map(minus), ...proprete.map(minus), ...amorces.map(minus), ...longrines.map(minus)],
    components: [mo('remblai', 'm³', 500)],
  },
  {
    lot: 'TERR',
    designation: 'Évacuation des terres excédentaires',
    unit: 'm³',
    rows: [...semelles, ...proprete, ...amorces, ...longrines],
    justification: 'Volume des ouvrages enterrés, remplacé par autant de terre sortie des fouilles.',
    components: [c('TRANSPORT', 'Chargement et transport', 'm³', 1, 4000, HYPOTHESE)],
  },
  { lot: 'TERR', designation: 'Traitement anti-termites du sol', unit: 'm²', rows: [row('Emprise', 1, 20, 12)], components: [mat('Produit et application', 'm²', 1, 1500, HYPOTHESE)] },

  // ── Fondations ──
  { lot: 'FOND', designation: 'Béton de propreté dosé à 150 kg/m³, épaisseur 10 cm', unit: 'm³', rows: proprete, components: [...beton(3), mo('béton de propreté', 'm³', 15000)] },
  {
    lot: 'FOND',
    designation: 'Béton armé dosé à 350 kg/m³ pour semelles isolées',
    unit: 'm³',
    justification: "Pré-dimensionnement pour un sol supposé de 1,5 bar. S1 2,50 × 2,50 × 0,60 m pour 900 kN ; S2 1,80 × 1,80 × 0,45 m pour 480 kN ; S3 1,30 × 1,30 × 0,35 m pour 240 kN. À valider par un ingénieur après étude de sol.",
    rows: semelles,
    components: ba(60, 2.5),
  },
  { lot: 'FOND', designation: 'Béton armé pour amorces de poteaux 35 × 35 cm', unit: 'm³', rows: amorces, components: ba(180, 11.5) },
  { lot: 'FOND', designation: 'Béton armé pour longrines 25 × 40 cm', unit: 'm³', rows: longrines, components: ba(120, 8) },
  {
    lot: 'FOND',
    designation: 'Soubassement en parpaings de 20 cm, enduit hydrofuge compris',
    unit: 'm²',
    rows: [row('Pourtour', 1, 64, '0.60'), row('Refends et cloisons', 1, 78, '0.60')],
    components: [mat('Parpaings de 20 cm remplis', 'U', 12.5, 400, ABD, 0.05), ...mortierM2(0.35, 0.05), mo('maçonnerie de soubassement', 'm²', 850)],
  },
  {
    lot: 'FOND',
    designation: 'Dallage du rez-de-chaussée, 10 cm, sur hérisson et polyane',
    unit: 'm²',
    rows: [row('Emprise', 1, 20, 12)],
    components: [mat('Hérisson de 20 cm', 'm³', 0.2, SABLE_M3, HYPOTHESE), mat('Film polyane', 'm²', 1.1, 700, HYPOTHESE), ...beton(6).map((b) => ({ ...b, quantity: String(Number(b.quantity) * 0.1) })), mat('Treillis soudé', 'kg', 2.2, ACIER_KG, ACIER_SRC), mo('dallage', 'm²', 1500)],
  },

  // ── Structure ──
  {
    lot: 'BA',
    designation: 'Poteaux en béton armé dosé à 350 kg/m³',
    unit: 'm³',
    justification: 'Sections dégressives : 35 × 35 cm au RDC et au R+1, 30 × 30 au R+2 et au R+3, 25 × 25 au R+4. Hauteur sous poutre 2,75 m. À vérifier par la note de calcul.',
    rows: [row('RDC et R+1', 48, '0.35', '0.35', '2.75'), row('R+2 et R+3', 48, '0.30', '0.30', '2.75'), row('R+4', 24, '0.25', '0.25', '2.80')],
    components: ba(180, 11.5),
  },
  { lot: 'BA', designation: 'Poutres de planchers 25 × 45 cm', unit: 'm³', rows: [row('152 ml par plancher, 4 planchers', 4, 152, '0.25', '0.45')], components: ba(130, 8.5) },
  { lot: 'BA', designation: 'Poutres sommier du R+4, 25 × 40 cm', unit: 'm³', rows: longrines, components: ba(120, 8.5) },
  {
    lot: 'BA',
    designation: 'Plancher à corps creux 16 + 4 cm, poutrelles et table de compression',
    unit: 'm²',
    rows: [row('Planchers hauts du RDC au R+3', 4, 20, 12), minus(row("Trémies d'escalier", 4, '2.60', '5.20'))],
    components: [mat('Hourdis de 16 cm', 'U', 5.5, 750, ABD, 0.03), ...beton(7).map((b) => ({ ...b, quantity: String(Number(b.quantity) * 0.08) })), mat('Aciers de poutrelles et de table', 'kg', 12, ACIER_KG, ACIER_SRC, 0.05), mat('Étaiement, réemplois compris', 'm²', 1, 3000, HYPOTHESE), mo('plancher', 'm²', 2000)],
  },
  {
    lot: 'BA',
    designation: 'Escalier en béton armé, paillasses et paliers',
    unit: 'm³',
    rows: [row('Volées, marches comprises', 8, '3.00', '1.20', '0.22'), row('Paliers', 8, '2.60', '1.20', '0.15')],
    components: [...ba(100, 7).filter((x) => !x.designation.includes('béton armé')), mo('escalier', 'm³', 50000)],
  },
  { lot: 'BA', designation: 'Linteaux 20 × 20 cm', unit: 'm³', rows: [row('36 ml par niveau', 5, 36, '0.20', '0.20')], components: ba(90, 10) },

  // ── Maçonnerie ──
  {
    lot: 'MAC',
    designation: 'Murs de façade en parpaings creux de 20 cm',
    unit: 'm²',
    rows: [row('Longueur hors poteaux', 5, '59.20', '2.75'), ...OUVERTURES_EXT],
    components: [mat('Parpaings creux de 20 cm', 'U', 12.5, 400, ABD, 0.05), ...mortierM2(0.15, 0.03), mo('maçonnerie en élévation', 'm²', 750)],
  },
  {
    lot: 'MAC',
    designation: 'Refends et cloisons en parpaings',
    unit: 'm²',
    justification: 'Parpaings de 20 cm pour les refends et de 15 cm pour les cloisons : prix moyen sur le plus épais, à détailler pour une offre.',
    rows: [row('Refends de 20 cm', 5, 20, '2.75'), row('Cloisons de 15 cm', 5, 58, '2.75'), ...PORTES_INT],
    components: [mat('Parpaings creux de 15 et 20 cm', 'U', 12.5, 370, ABD, 0.05), ...mortierM2(0.13, 0.03), mo('maçonnerie en élévation', 'm²', 750)],
  },

  // ── Enduits ──
  { lot: 'ENDUIT', designation: 'Enduit extérieur au mortier de ciment', unit: 'm²', rows: [...FACADE, ...OUVERTURES_EXT], components: [...mortierM2(0.16, 0.025), mo('enduit', 'm²', 1000)] },
  { lot: 'ENDUIT', designation: 'Enduit intérieur sur murs', unit: 'm²', rows: [...MURS_INT, ...OUVERTURES_EXT, ...PORTES_INT_2FACES], components: [...mortierM2(0.13, 0.02), mo('enduit', 'm²', 1000)] },
  { lot: 'ENDUIT', designation: 'Enduit en sous-face des planchers et de l\'escalier', unit: 'm²', rows: [row('Planchers', 4, 20, 12), minus(row('Trémies', 4, '2.60', '5.20')), row('Volées et paliers', 16, '2.80', '1.20')], components: [...mortierM2(0.13, 0.02), mo('enduit sous-face', 'm²', 1250)] },

  // ── Étanchéité ──
  { lot: 'ETANCH', designation: 'Étanchéité des salles d\'eau, relevés compris', unit: 'm²', rows: [row('Sols des 15 salles de bain', 15, '2.50', '2.00'), row('Sols des 5 WC séparés', 5, '1.50', '1.20')], components: fp(6000, 1500, 'm²', 'Produit d\'étanchéité') },

  // ── Charpente et couverture ──
  { lot: 'CHARP', designation: 'Charpente métallique, fermes et pannes, traitée antirouille', unit: 'm²', justification: 'Deux versants à 20 %, débords de 50 cm : 21 × 13 m en plan, × 1,02 pour la pente. À vérifier par le fabricant.', rows: [row('Surface de toiture', 1, 21, '13.26')], components: fp(15000, 3000, 'm²') },
  { lot: 'COUV', designation: 'Couverture en tuile métallique', unit: 'm²', rows: [row('Surface de toiture', 1, 21, '13.26')], components: [mat('Tuile métallique', 'm²', 1, 9500, HYPOTHESE, 0.05), mat('Fixations et joints', 'm²', 1, 300, HYPOTHESE), mo('pose', 'm²', 1500, HYPOTHESE)] },
  { lot: 'COUV', designation: 'Faîtage en tuile métallique', unit: 'ml', rows: [row('Faîtage', 1, 21)], components: fp(6000, 1000, 'ml') },
  { lot: 'COUV', designation: 'Rives', unit: 'ml', rows: [row('4 rives', 4, '6.63')], components: fp(5000, 1000, 'ml') },
  { lot: 'COUV', designation: 'Gouttières', unit: 'ml', rows: [row('2 longs pans', 2, 21)], components: fp(8000, 1500, 'ml') },
  { lot: 'COUV', designation: 'Descentes d\'eaux pluviales', unit: 'ml', rows: [row('6 descentes', 6, 17)], components: fp(6000, 1000, 'ml') },

  // ── Menuiseries ──
  { lot: 'MENUIS_MET', designation: "Porte d'immeuble métallique, 1,80 × 2,40 m", unit: 'U', rows: [val('Hall', 1)], components: fp(450000, 50000, 'U') },
  { lot: 'MENUIS_MET', designation: 'Portes palières métalliques, 1,00 × 2,10 m', unit: 'U', rows: [val('1 par logement', 10)], components: fp(180000, 20000, 'U') },
  { lot: 'MENUIS_MET', designation: "Garde-corps et main courante d'escalier", unit: 'ml', rows: [row('Volées et paliers', 8, '4.00')], components: fp(45000, 5000, 'ml') },
  { lot: 'MENUIS_BOIS', designation: 'Portes intérieures isoplanes, 0,80 × 2,10 m', unit: 'U', rows: [val('11 par niveau', 11, 5)], components: fp(75000, 10000, 'U') },
  { lot: 'MENUIS_ALU', designation: 'Fenêtres en aluminium vitrées, 1,20 × 1,20 m', unit: 'U', rows: [val('9 par niveau', 9, 5)], components: fp(120000, 10000, 'U') },
  { lot: 'MENUIS_ALU', designation: 'Fenêtres de cuisine et de salle d\'eau, 0,80 × 0,80 m', unit: 'U', rows: [val('6 par niveau', 6, 5)], components: fp(55000, 7000, 'U') },

  // ── Revêtements ──
  { lot: 'REVET', designation: 'Carrelage de sol en grès, chape comprise', unit: 'm²', rows: [val('Surface utile par niveau, murs et trémie déduits', 200, 5)], components: [mat('Carrelage', 'm²', 1, 9000, HYPOTHESE, 0.05), ...mortierM2(0.2, 0.03), mo('pose', 'm²', 2500, HYPOTHESE)] },
  { lot: 'REVET', designation: "Carrelage des marches et paliers d'escalier", unit: 'm²', rows: [row('Volées', 8, '3.00', '1.20'), row('Paliers', 8, '2.60', '1.20')], components: [mat('Carrelage', 'm²', 1, 9000, HYPOTHESE, 0.1), ...mortierM2(0.2, 0.03), mo('pose', 'm²', 3500, HYPOTHESE)] },
  { lot: 'REVET', designation: 'Faïence murale des salles d\'eau et des cuisines', unit: 'm²', rows: [row('Salles de bain, hauteur 2 m', 15, '9.00', '2.00'), row('WC séparés, hauteur 1,50 m', 5, '5.40', '1.50'), row('Crédences de cuisine', 10, '3.00', '0.60')], components: [mat('Faïence', 'm²', 1, 11000, HYPOTHESE, 0.05), mat('Colle et joints', 'm²', 1, 1500, HYPOTHESE), mo('pose', 'm²', 3000, HYPOTHESE)] },
  { lot: 'REVET', designation: 'Plinthes', unit: 'ml', rows: [val('Par niveau', 170, 5)], components: fp(1500, 500, 'ml') },
  { lot: 'FAUX_PLAFOND', designation: 'Faux plafond sous toiture au R+4', unit: 'm²', rows: [row('Emprise', 1, 20, 12)], components: fp(7000, 1500, 'm²') },

  // ── Peinture ──
  { lot: 'PEINT', designation: 'Peinture extérieure, deux couches sur impression', unit: 'm²', rows: [...FACADE, ...OUVERTURES_EXT], components: fp(1800, 700, 'm²', 'Peinture et impression') },
  { lot: 'PEINT', designation: 'Peinture intérieure des murs et des plafonds', unit: 'm²', rows: [...MURS_INT, ...OUVERTURES_EXT, ...PORTES_INT_2FACES, row('Plafonds', 5, 20, 12), minus(row('Trémies', 4, '2.60', '5.20'))], components: fp(1200, 600, 'm²', 'Peinture et impression') },

  // ── Électricité ──
  { lot: 'ELEC', designation: 'Tableau de logement et disjoncteurs', unit: 'U', rows: [val('1 par logement', 10)], components: fp(90000, 20000, 'U') },
  { lot: 'ELEC', designation: 'Points lumineux, filerie et gaine comprises', unit: 'U', rows: [val('Par F4', 14, 5), val('Par F3', 11, 5), val('Parties communes', 12)], components: fp(12000, 3000, 'U') },
  { lot: 'ELEC', designation: 'Prises de courant, filerie et gaine comprises', unit: 'U', rows: [val('Par F4', 18, 5), val('Par F3', 14, 5)], components: fp(10000, 3000, 'U') },
  { lot: 'ELEC', designation: "Colonne montante, compteurs et mise à la terre", unit: 'FF', rows: [val('Forfait', 1)], components: fp(2800000, 400000, 'FF') },

  // ── Plomberie ──
  { lot: 'PLOMB', designation: 'WC à l\'anglaise, chasse comprise', unit: 'U', rows: [val('2 par logement', 2, 10)], components: fp(120000, 20000, 'U') },
  { lot: 'PLOMB', designation: 'Lavabos, robinetterie comprise', unit: 'U', rows: [val('Par F4', 2, 5), val('Par F3', 1, 5)], components: fp(70000, 15000, 'U') },
  { lot: 'PLOMB', designation: 'Douches, robinetterie comprise', unit: 'U', rows: [val('Par F4', 2, 5), val('Par F3', 1, 5)], components: fp(60000, 15000, 'U') },
  { lot: 'PLOMB', designation: 'Éviers de cuisine, robinetterie comprise', unit: 'U', rows: [val('1 par logement', 10)], components: fp(85000, 15000, 'U') },
  { lot: 'PLOMB', designation: 'Réseaux d\'eau et d\'évacuation, colonnes comprises', unit: 'FF', rows: [val('Forfait', 1)], components: fp(4500000, 1500000, 'FF') },
  { lot: 'PLOMB', designation: 'Réservoir d\'eau en toiture et groupe de pompage', unit: 'FF', rows: [val('Forfait', 1)], components: fp(2500000, 300000, 'FF') },

  // ── Assainissement et abords ──
  { lot: 'ASSAIN', designation: 'Fosse septique en béton armé', unit: 'U', rows: [val('Pour 10 logements', 1)], components: fp(2800000, 500000, 'U') },
  { lot: 'ASSAIN', designation: 'Puisard', unit: 'U', rows: [val('Pour 10 logements', 1)], components: fp(1200000, 300000, 'U') },
  { lot: 'ASSAIN', designation: 'Regards et canalisations enterrées', unit: 'U', rows: [val('Regards', 12)], components: fp(45000, 15000, 'U') },
  { lot: 'VRD', designation: 'Clôture en parpaings, hauteur 2,00 m, fondation comprise', unit: 'ml', rows: [row('Pourtour de la parcelle', 1, 100)], components: fp(45000, 12000, 'ml') },
  { lot: 'VRD', designation: "Portail métallique d'accès", unit: 'U', rows: [val('Accès', 1)], components: fp(650000, 50000, 'U') },
  { lot: 'VRD', designation: 'Dallage des abords, 10 cm', unit: 'm²', rows: [row('Accès et cour', 1, 30, 4)], components: fp(14000, 2000, 'm²') },
];

export const INDICATIVE_LIST_NAME = 'Prix indicatifs Tchad';
export const INDICATIVE_LIST_NOTES = "PRIX À TITRE INDICATIF, non officiels : ajustez-les selon vos prix réels avant toute offre. Les prix changent vite et varient selon la ville et le fournisseur.";
const WORK_SOURCE = "Prix indicatif · déboursé estimé, hors frais et marge, à ajuster selon vos prix réels";

/** Bibliothèque proposée : matériaux relevés, puis déboursé de chaque ouvrage de l'exemple. Rien n'y est officiel. */
export function indicativePriceItems() {
  const items: Array<{ kind: 'MATERIAL' | 'WORK'; code: string; designation: string; unit: string; price: string; source: string }> = [];
  const seen = new Set<string>();
  for (const component of R4_LINES.flatMap((l) => l.components)) {
    if (component.kind !== 'MATERIAL' || component.source === HYPOTHESE || seen.has(component.designation)) continue;
    seen.add(component.designation);
    items.push({ kind: 'MATERIAL', code: `IND-M${String(seen.size).padStart(2, '0')}`, designation: component.designation, unit: component.unit, price: component.unitPrice, source: component.source ?? WORK_SOURCE });
  }
  R4_LINES.forEach((l, i) => {
    const price = roundTo(D(computeAnalysis({ components: l.components }).dsUnit), 2).toString();
    items.push({ kind: 'WORK', code: `IND-O${String(i + 1).padStart(2, '0')}`, designation: l.designation, unit: l.unit, price, source: WORK_SOURCE });
  });
  return items;
}
